/**
 * The word "reach" means **distinct addresses**, and this is the one place the
 * campaign screens get that phrase from.
 *
 * ## Why a module for two strings
 *
 * Reach is the number every campaign screen is built around and the number a
 * person commits to a send on, and it is the one most easily misread: a segment
 * of 1,278 people reaches 526 by email, so "reach" read as "the segment" doubles
 * the audience in somebody's head at the moment they press Schedule. Slice 2
 * settled the arithmetic and slice 17 settled the wording — the screen has to
 * say *distinct addresses*, in those words, rather than leave a reader to infer
 * distinctness from the fact that two counts are printed beside each other.
 *
 * That sentence appears on the create screen and again on the campaign's own
 * screen, which are two files, and a wording rule enforced by two independent
 * copies of a string is a wording rule that lasts until somebody edits one of
 * them. `CHANNEL_ADDRESS_LABEL` in `lib/campaign-display` is the neighbouring
 * table and deliberately not this one: its values are `'an email address'` and
 * `'a phone number'`, shaped for "everyone in this segment who has ___", which
 * is a singular indefinite and cannot be pluralised by adding a letter to it.
 * Reaching for it here is what produced "distinct an email addresses" in a
 * draft of this slice, which is the concrete reason this file exists rather than
 * a second lookup into that one.
 *
 * **It is deliberately not in `lib/`.** Nothing outside `routes/campaigns`
 * counts a reach — `/analytics` reports on periods and has its own reach panel
 * and its own vocabulary — and slice 17 ran four agents over four route folders
 * at once, so a new shared file under three of them is a merge nobody asked for.
 * If a fourth screen ever needs the phrase, `lib/campaign-display.ts` is where
 * it goes, next to the table that could not supply it.
 */
import type { Channel } from '@wend/shared';

/**
 * What a reach counts, per channel, as a plural noun that can follow "distinct".
 *
 * Keyed by `Channel` rather than typed as a free string, so a channel added to
 * the contract without a word here is a compile error rather than a screen that
 * quietly says "reach is the distinct undefined in the segment".
 */
export const REACH_ADDRESS_PLURAL: Readonly<Record<Channel, string>> = {
  email: 'email addresses',
  whatsapp: 'phone numbers',
};
